/**
 * =============================================================================
 * ROASTBREW - PLAYLIST PLAYER  ·  /playlist/:code/:item
 * =============================================================================
 *
 * Working through a playlist. `item` is the position in the playlist (1, 2, 3 …),
 * counting lessons AND notes, so a note has its own address like a lesson.
 *
 * Left:  the playlist as a numbered margin — the way to move between items.
 *        Watched lessons are ticked and struck through; notes show a pencil.
 * Right: the current item —
 *          a lesson → the player, then the lesson tabs (Video / Homework / …)
 *          a note   → the note, in the player's place, with Continue
 *        Previous / Next sit under the player.
 *
 * ⚠️ THE PLAYER IS A PLACEHOLDER. The real lesson pages are six engine × device
 * variants (see VideoViewRouter.jsx); wiring one in is a separate step. Nothing
 * here plays video, loads /video/:id, reports progress or refreshes the algorithm.
 *
 * NOT WIRED YET. Content comes from data/playlistSample.js; "watched" is the
 * sample set there (real progress comes from progressByVideo).
 * Access: gated in App.jsx (RequireFeature 'playlists'). No tier logic here.
 * =============================================================================
 */

import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTheme } from './ThemeContext';
import SearchBar from './SearchBar';
import { getSamplePlaylist, numberItems, SAMPLE_WATCHED } from './data/playlistSample';

const SANS = '"DM Sans", sans-serif';
const SERIF = '"Cormorant Garamond", serif';
const PENCIL = (size) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 20h4L19 9l-4-4L4 16v4z" /><path d="M13.5 6.5l4 4" />
  </svg>
);
const TABS = [['video', 'Video'], ['homework', 'Homework'], ['answerkey', 'Answer Key Video'], ['answerkeypdf', 'Answer Key PDF']];

export default function PlaylistPlayPage({ isMobile = false, isTablet = false }) {
  const { colors, isLight } = useTheme();
  const navigate = useNavigate();
  const { code, item } = useParams();
  const [tab, setTab] = useState('video');

  const playlist = getSamplePlaylist(code);
  const items = numberItems(playlist ? playlist.items : []);
  const pos = parseInt(item, 10);
  const cur = Number.isFinite(pos) && pos >= 1 && pos <= items.length ? pos - 1 : -1;
  const c = cur >= 0 ? items[cur] : null;

  const edge = isLight ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.08)';
  const rule = isLight ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.06)';
  const track = isLight ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.1)';
  const onAccent = isLight ? '#FFFFFF' : '#0D0D0D';
  const twoCol = !isMobile && !isTablet;
  const go = (n) => navigate(`/playlist/${code}/${n}`);

  const page = {
    padding: isMobile ? '80px 16px 32px' : isTablet ? '80px 32px 32px' : '32px 48px',
    minHeight: '100vh', width: '100%', maxWidth: '100%', overflowX: 'hidden', boxSizing: 'border-box',
    background: colors.bg, color: colors.text, animation: 'fadeIn 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
  };

  // Not a playlist, or not an item in it: say so and offer a way back.
  if (!playlist || !c) {
    return (
      <div className="pl-play" style={page}>
        <div style={{ maxWidth: '640px', margin: '40px auto', display: 'flex', flexDirection: 'column', gap: '18px' }}>
          <h1 style={{ margin: 0, fontFamily: SERIF, fontWeight: 300, fontSize: '40px' }}>
            {playlist ? `There’s no item ${item} in this playlist` : 'We couldn’t find this playlist'}
          </h1>
          <p style={{ margin: 0, fontFamily: SANS, fontSize: '15px', lineHeight: 1.6, color: colors.muted }}>
            {playlist ? `“${playlist.title}” has ${items.length} items.` : `There’s no playlist with the code ${code}.`}
          </p>
          <button type="button" onClick={() => navigate(playlist ? `/playlist/${code}/1` : '/playlist')} style={{
            alignSelf: 'flex-start', height: '46px', padding: '0 22px', border: 0, borderRadius: '999px', background: colors.accent, color: onAccent,
            fontFamily: SANS, fontSize: '14.5px', fontWeight: 600, cursor: 'pointer',
          }}>{playlist ? 'Start from the first item' : 'Go to your playlists'}</button>
        </div>
      </div>
    );
  }

  const lessons = items.filter((i) => i.type === 'lesson');
  const watched = (it, i) => (it.type === 'lesson' ? SAMPLE_WATCHED.has(it.videoId) : i < cur);   // sample only
  const doneCount = lessons.filter((l) => SAMPLE_WATCHED.has(l.videoId)).length;
  const isNote = c.type === 'note';
  // For a note: the lessons either side of it, by number.
  const prevLesson = items.slice(0, cur).reverse().find((i) => i.type === 'lesson');
  const nextLesson = items.slice(cur + 1).find((i) => i.type === 'lesson');
  const nextItem = items[cur + 1] || null;

  const head = isNote ? 'A note' : `§ ${c.lessonNo} of ${lessons.length}`;
  const sub = isNote
    ? (prevLesson && nextLesson ? `Between § ${prevLesson.lessonNo} and § ${nextLesson.lessonNo}` : nextLesson ? `Before § ${nextLesson.lessonNo}` : prevLesson ? `After § ${prevLesson.lessonNo}` : '')
    : `From ${c.course} · ${c.section}`;
  const continueLabel = nextItem ? (nextItem.type === 'note' ? 'Continue to the next note' : `Continue to § ${nextItem.lessonNo}`) : null;

  // ── The numbered margin ──
  const margin = (
    <nav aria-label="Playlist" style={{ display: 'flex', flexDirection: 'column', gap: '18px', padding: '24px 18px 18px', borderRadius: '18px', background: colors.cardBg || colors.bgAlt, border: `1px solid ${edge}` }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <button type="button" onClick={() => navigate(`/playlist/${code}`)} style={{
          alignSelf: 'flex-start', display: 'inline-flex', alignItems: 'center', gap: '6px', minHeight: '32px', padding: 0, border: 0, background: 'transparent',
          fontFamily: SANS, fontSize: '11px', fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', color: colors.muted, cursor: 'pointer',
        }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M15 6l-6 6 6 6" /></svg>
          Playlist
        </button>
        <div style={{ fontFamily: SERIF, fontSize: '26px', lineHeight: 1.05 }}>{playlist.title}</div>
        <div style={{ fontFamily: SERIF, fontStyle: 'italic', fontSize: '15px', color: colors.textSecondary }}>by {playlist.author}</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '4px' }}>
          <div style={{ height: '3px', borderRadius: '2px', background: track, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${Math.round((doneCount / Math.max(lessons.length, 1)) * 100)}%`, background: colors.accent }} />
          </div>
          <span style={{ fontFamily: SANS, fontSize: '12px', color: colors.muted }}>{doneCount} of {lessons.length} watched</span>
        </div>
      </div>
      <span style={{ padding: '0 8px', fontFamily: SANS, fontSize: '12px', color: colors.muted }}>Choose any lesson to play it.</span>
      <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column' }}>
        {items.map((it, i) => {
          const isCur = i === cur;
          const done = !isCur && watched(it, i);
          const numColor = isCur ? colors.accent : done ? colors.muted : colors.textSecondary;
          const shown = it.type === 'note' ? (it.text.length > 46 ? `${it.text.slice(0, 44)}…` : it.text) : it.title;
          return (
            <li key={it.type === 'note' ? it.id : it.videoId}>
              <button type="button" className="pl-mrow" aria-current={isCur ? 'step' : undefined} onClick={() => go(i + 1)} style={{
                display: 'flex', alignItems: 'flex-start', gap: '16px', width: '100%', minHeight: '56px', padding: '8px', border: 0,
                borderBottom: `1px solid ${rule}`, borderRadius: '8px', background: 'transparent', color: colors.text, textAlign: 'left', cursor: 'pointer',
              }}>
                {it.type === 'lesson' ? (
                  <span style={{ flexShrink: 0, width: '44px', textAlign: 'right', fontFamily: SERIF, fontWeight: 300, lineHeight: 1, fontSize: isCur ? '46px' : '30px', color: numColor, opacity: done ? 0.55 : 1 }}>{it.lessonNo}</span>
                ) : (
                  <span style={{ flexShrink: 0, width: '44px', display: 'flex', justifyContent: 'flex-end', paddingTop: '6px', color: numColor }}>{PENCIL(15)}</span>
                )}
                <span style={{ flex: 1, minWidth: 0, paddingTop: '4px', fontFamily: SANS }}>
                  <span style={{
                    display: 'block', lineHeight: 1.3, fontStyle: it.type === 'note' ? 'italic' : 'normal',
                    fontSize: isCur ? '15px' : '13.5px', fontWeight: isCur ? 600 : 400,
                    color: isCur ? colors.text : done ? colors.muted : colors.textSecondary,
                    textDecoration: done ? 'line-through' : 'none', textDecorationColor: `${colors.accent}80`,
                  }}>{shown}</span>
                  <span style={{ display: 'block', marginTop: '3px', fontSize: '11px', color: colors.muted }}>{it.type === 'note' ? `Note from ${playlist.author}` : it.length}</span>
                </span>
                {done && (
                  <svg width="15" height="15" viewBox="0 0 24 24" fill={colors.accent} style={{ flexShrink: 0, alignSelf: 'center' }} aria-label="Watched"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" /></svg>
                )}
                {isCur && <span aria-hidden="true" style={{ flexShrink: 0, alignSelf: 'center', width: '8px', height: '8px', borderRadius: '50%', background: colors.accent, boxShadow: `0 0 10px ${colors.accentGlow || colors.accent}` }} />}
              </button>
            </li>
          );
        })}
      </ol>
    </nav>
  );

  // ── The current item ──
  const content = (
    <div style={{ minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '10px' }}>
        <span style={{ fontFamily: SANS, fontSize: '11px', fontWeight: 700, letterSpacing: '0.22em', textTransform: 'uppercase', color: colors.muted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{playlist.title}</span>
        <span style={{ flex: 1, height: '1px', background: edge }} />
        <span style={{ flexShrink: 0, fontFamily: SERIF, fontStyle: 'italic', fontSize: '17px', color: colors.accent }}>{head}</span>
      </div>
      <h1 style={{ margin: '0 0 4px', fontFamily: SERIF, fontWeight: 400, fontSize: isMobile ? '28px' : '34px', lineHeight: 1.15 }}>
        {isNote ? `A note from ${playlist.author}` : c.title}
      </h1>
      {sub && <div style={{ margin: '0 0 16px', fontFamily: SANS, fontSize: '12.5px', color: colors.muted }}>{sub}</div>}

      {isNote ? (
        <div style={{
          position: 'relative', boxSizing: 'border-box', minHeight: isMobile ? 'auto' : undefined, aspectRatio: isMobile ? 'auto' : '16 / 9',
          display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '22px', padding: isMobile ? '28px 24px' : '48px 56px',
          borderRadius: '16px', background: colors.cardBg || colors.bgAlt, border: `1px solid ${colors.accent}47`,
        }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', fontFamily: SANS, fontSize: '11px', fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', color: colors.accent }}>
            {PENCIL(13)} A note from {playlist.author}
          </span>
          <p style={{ margin: 0, fontFamily: SERIF, fontSize: isMobile ? '24px' : '30px', lineHeight: 1.35, whiteSpace: 'pre-line' }}>{c.text}</p>
          {continueLabel && (
            <button type="button" onClick={() => go(cur + 2)} style={{
              alignSelf: 'flex-start', display: 'inline-flex', alignItems: 'center', gap: '10px', height: '48px', padding: '0 24px', border: 0,
              borderRadius: '999px', background: colors.accent, color: onAccent, fontFamily: SANS, fontSize: '14.5px', fontWeight: 600, cursor: 'pointer',
            }}>
              {continueLabel}
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M9 6l6 6-6 6" /></svg>
            </button>
          )}
        </div>
      ) : (
        // PLACEHOLDER PLAYER — the real one is wired in later (see the header note).
        <div role="img" aria-label={`Video player placeholder for “${c.title}”`} style={{
          position: 'relative', aspectRatio: '16 / 9', borderRadius: isMobile ? '12px' : '16px', overflow: 'hidden',
          background: 'radial-gradient(ellipse at 30% 30%, #1E2638 0%, #0A0C12 70%)', display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', gap: '14px',
        }}>
          <span aria-hidden="true" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '76px', height: '76px', borderRadius: '50%', background: 'rgba(245,245,240,0.12)', border: '1px solid rgba(245,245,240,0.25)' }}>
            <svg width="26" height="26" viewBox="0 0 24 24" fill="rgba(245,245,240,0.8)" style={{ marginLeft: '4px' }}><path d="M8 5v14l11-7z" /></svg>
          </span>
          <span style={{ fontFamily: SANS, fontSize: '13px', color: 'rgba(245,245,240,0.7)' }}>Video player placeholder · {c.length}</span>
        </div>
      )}

      {/* Previous / Next */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', marginTop: '14px' }}>
        <button type="button" disabled={cur === 0} onClick={() => go(cur)} style={{
          display: 'inline-flex', alignItems: 'center', gap: '10px', height: '46px', padding: '0 18px', borderRadius: '999px',
          border: `1px solid ${edge}`, background: 'transparent', color: colors.text, fontFamily: SANS, fontSize: '14px', fontWeight: 500,
          cursor: cur === 0 ? 'default' : 'pointer', opacity: cur === 0 ? 0.4 : 1,
        }}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M15 6l-6 6 6 6" /></svg>
          Previous
        </button>
        <span style={{ fontFamily: SANS, fontSize: '13px', color: colors.muted }}>{head}</span>
        <button type="button" disabled={!nextItem} onClick={() => go(cur + 2)} style={{
          display: 'inline-flex', alignItems: 'center', gap: '10px', height: '46px', padding: '0 20px', border: 0, borderRadius: '999px',
          background: colors.accent, color: onAccent, fontFamily: SANS, fontSize: '14px', fontWeight: 600,
          cursor: nextItem ? 'pointer' : 'default', opacity: nextItem ? 1 : 0.4,
        }}>
          Next
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M9 6l6 6-6 6" /></svg>
        </button>
      </div>

      {/* Lesson tabs — a lesson only. Their content arrives with the real lesson data. */}
      {!isNote && (
        <div style={{ marginTop: isMobile ? '20px' : '28px' }}>
          <div role="tablist" aria-label="Lesson" style={{ display: 'flex', gap: '4px', padding: '4px', margin: isMobile ? '4px 0 20px' : '8px 0 24px', borderRadius: '10px', background: isLight ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.04)' }}>
            {TABS.map(([k, label]) => (
              <button key={k} type="button" role="tab" aria-selected={tab === k} onClick={() => setTab(k)} style={{
                flex: 1, padding: isMobile ? '10px 6px' : '12px', border: 0, borderRadius: '8px', background: tab === k ? (colors.cardBg || colors.bgAlt) : 'transparent',
                color: tab === k ? colors.text : colors.muted, fontFamily: SANS, fontSize: isMobile ? '11px' : '13px', fontWeight: 500, cursor: 'pointer',
              }}>{label}</button>
            ))}
          </div>
          <h3 style={{ margin: '0 0 12px', fontFamily: SERIF, fontWeight: 400, fontSize: isMobile ? '16px' : '18px' }}>
            {tab === 'video' ? 'About This Episode' : TABS.find(([k]) => k === tab)[1]}
          </h3>
          <p style={{ margin: 0, fontFamily: SANS, fontSize: isMobile ? '14px' : '15px', lineHeight: 1.7, color: colors.textSecondary }}>
            This fills in from the lesson’s details once the page is connected to the lesson.
          </p>
        </div>
      )}
    </div>
  );

  return (
    <div className="pl-play" style={page}>
      {/* Page search — the same band the lesson pages use. */}
      <div style={{
        margin: isMobile ? '-80px -16px 24px' : isTablet ? '-80px -32px 24px' : '-32px -48px 32px',
        padding: isMobile ? '72px 16px 16px' : isTablet ? '72px 32px 18px' : '24px 48px',
        background: colors.bgAlt, borderBottom: `1px solid ${isLight ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.06)'}`,
      }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          <SearchBar variant="video" placeholder="Search courses, lessons, topics..." />
        </div>
      </div>
      <div style={{
        maxWidth: '1400px', margin: '0 auto', display: 'grid', gap: isMobile ? '24px' : '40px', alignItems: 'start',
        gridTemplateColumns: twoCol ? '340px minmax(0, 1fr)' : '1fr',
      }}>
        {twoCol ? <>{margin}{content}</> : <>{content}{margin}</>}
      </div>

      <style>{`
        .pl-play button:focus-visible { outline: 2px solid ${colors.accent}; outline-offset: 3px; }
        .pl-mrow:hover { background: ${isLight ? 'rgba(0,0,0,0.03)' : 'rgba(255,255,255,0.04)'} !important; }
        @media (prefers-reduced-motion: reduce) { .pl-play, .pl-play * { animation: none !important; transition: none !important; } }
      `}</style>
    </div>
  );
}
